<template>
  <main class="w-full">
    <section class="w-full max-sm:px-6 max-xl:px-13 py-12 md:py-24 lg:bg-grey-dark-900">
      <div class="max-w-content mx-auto flex flex-col lg:flex-row items-center text-center lg:text-left gap-14 lg:gap-16">
        <div class="flex flex-col gap-4 md:gap-10 lg:gap-12 max-w-3xl lg:max-w-none lg:flex-[1_0_0]">
          <p class="text-base font-semibold text-grey-dark-300 w-full">Payment plans</p>
          <h1 class="text-4xl lg:text-6xl font-semibold text-grey-dark-50 w-full">
            Get paid. Without the admin.
          </h1>
          <p class="text-base lg:text-xl font-normal text-grey-dark-300 w-full">
            LayUp handles your payment plans so you can focus on running your business — not chasing invoices.
          </p>
          <div class="flex flex-col-reverse sm:flex-row items-center justify-center lg:justify-start gap-4 mt-6">
            <router-link :to="{ name: 'business-enquiry' }" class="w-full sm:w-auto">
              <Button size="2xl" class="w-full sm:w-auto py-6">Talk to sales</Button>
            </router-link>
          </div>
        </div>
        <AnimationComponent class="lg:shrink-0 lg:w-[634px] lg:h-[559px]">
          <img :src="MerchantPaymentPlanProductImage" alt="Merchant Payment Plan Product Image" class="w-full h-full object-cover" />
        </AnimationComponent>
      </div>
    </section>
    <Separator class="bg-grey-dark-800" />
    <section class="w-full py-24 overflow-clip">
      <MerchantLogoCarousel variant="light" />
    </section>
    <Separator class="bg-grey-dark-800" />
    <div class="max-sm:mx-6 max-xl:mx-13 lg:hidden">
      <Separator class="bg-grey-dark-800" />
    </div>
    <FeaturesSection
      :features="features"
      title="Your customers pay over time.<br>You get paid on time."
      description="No spreadsheets, no awkward follow-ups. LayUp automates the entire payment plan process, from setup to collection."
      :use-alternative-tag-text-color="true"
      :items-per-row="isDesktop ? 4 : 2"
      :add-padding-x="!isDesktop"
    />
    <div class="max-w-content mx-auto mx-8">
      <Separator class="bg-grey-dark-800" />
    </div>
    <section class="w-full pt-[50px] lg:pt-24 max-sm:px-6 max-xl:px-13">
      <div class="max-w-content mx-auto">
        <AnimationComponent>
          <div class="flex flex-col items-center justify-center text-center mb-16">
            <p class="max-w-3xl text-base lg:text-xl font-light text-grey-dark-300 mb-3">
              Manage customer relations
            </p>
            <h2 class="max-w-3xl text-3xl lg:text-4xl font-semibold text-white mb-[26px]">
              Run your collections on autopilot
            </h2>
            <p class="max-w-3xl text-base lg:text-xl font-light text-grey-dark-300">
              One dashboard. Every customer. Full visibility.
              <br><br>
              LayUp's self-service portal connects with your CRM and ERP, so your team always knows where things stand.
            </p>
          </div>
        </AnimationComponent>
        <AnimationComponent>
          <div class="flex flex-col items-center justify-center text-center gap-10">
            <img
              :src="automateTrackingReportingImage"
              alt="Automate tracking, reconciliation and reporting"
            />
          </div>
        </AnimationComponent>
      </div>
    </section>
    <PaymentMethodsSection
      title="Select from multiple payment methods"
      description="Enable the payment options that best suit your customer audience."
      link-to-page="business-pricing-layby"
    />
    <TitleFeaturesSection
      :features="cloudPaymentFeatures"
      tag="Key tech features"
      title="Payments that actually work for your business"
      description="Offer shoppers a tailored payment experience."
    />
    <div class="max-sm:mx-6 max-xl:mx-13 lg:hidden">
      <Separator class="bg-grey-dark-800" />
    </div>
    <ApiFeatures
      :use-alternative-tag-text-color="true"
      :items-per-row="isDesktop ? 3 : 2"
      class="lg:px-24"
      :add-padding-x="!isDesktop"
    />
    <div class="max-sm:mx-6 max-xl:mx-13 lg:hidden">
      <Separator class="bg-grey-dark-800" />
    </div>
    <section class="w-full max-md:py-[50px] text-center md:text-left">
      <CtaSection
        title="Get started with LayUp"
        description="Low transaction fees and flexible settlements."
        :reverse-buttons="true"
        :learn-more-variant="isDesktop ? 'outline' : 'white'"
        :reduce-padding-top-bottom="!isDesktop"
        :add-padding-x="!isDesktop"
      />
    </section>
    <LatestBlogs :is-dark-background="true" class="md:pt-24" />
  </main>
</template>

<script setup lang="ts">
import automateTrackingReportingImage from '@/assets/images/business/automate-tracking-reporting.webp';
import MerchantPaymentPlanProductImage from '@/assets/images/business/merchant-payment-plan.webp';
import AnimationComponent from '@/components/AnimationComponent.vue';
import CtaSection from '@/components/business/CtaSection.vue';
import FeaturesSection from '@/components/business/FeaturesSection.vue';
import PaymentMethodsSection from '@/components/business/PaymentMethodsSection.vue';
import TitleFeaturesSection from '@/components/business/TitleFeaturesSection.vue';
import MerchantLogoCarousel from '@/components/MerchantLogoCarousel.vue';
import { Button } from '@/components/ui/button';
import Separator from '@/components/ui/separator/SeparatorComponent.vue';
import ApiFeatures from '../../components/business/FeatureApi.vue';
import Umbrella from '@/assets/images/umbrella.svg'
import UserPlus from '@/assets/images/user-plus.svg'
import PresentationChart from '@/assets/images/presentation-chart.svg'
import Certificate from '@/assets/images/certificate.svg'
import ShoppingCart from '@/assets/images/shopping-cart.svg'
import LatestBlogs from '@/common/LatestBlogs.vue';
import { useMediaQuery } from '@vueuse/core';

const isDesktop = useMediaQuery('(min-width: 1024px)');

const features = [
  {
    icon: 'Calendar',
    title: 'Bookings secured upfront ',
    description: 'Stop losing jobs to no-shows. Collect deposits instantly and lock in your customers before the work begins.',
  },
  {
    image: UserPlus,
    title: 'Turn browsers into buyers ',
    description: 'Give customers a reason to say yes. Flexible payment options reduce price resistance and increase conversions.',
  },
  {
    image: Umbrella,
    title: 'Built for service businesses',
    description: 'Whether you\'re in travel or medical, LayUp fits your workflow without changing how you work.',
  },
  {
    icon: 'TrendingUp',
    title: 'More revenue, more control',
    description: 'Unlock new income streams with payment plans your customers actually want to use.',
  },
];

const cloudPaymentFeatures = [
  {
    image: PresentationChart,
    useImageIcon: true,
    title: 'Automated reporting, reconciliation, and tracking.',
  },
  {
    image: Certificate,
    useImageIcon: true,
    title: 'Software licensing included — no hidden costs.',
  },
  {
    image: ShoppingCart,
    useImageIcon: true,
    title: 'End-to-end customer journey support.',
  },
];
</script>
